<template>
  <mf-loading-dialog :loading="loadingPage">
    <base-page-layout ref="basePage" :title="pageTitle">
      <stepper :steppers="steppers" :step="1" />

      <title-description
        class="mb-4"
        :status="campaign.status"
        :title.sync="campaign.title"
        :description.sync="campaign.description"
        :title-error="$v.campaign.title && $v.campaign.title.$error"
        :title-error-message="errorTitle"
        :description-error="$v.campaign.description && $v.campaign.description.$error"
        :description-error-message="errorDescription"
        :disable-title="isMariaCampaignFinished"
        :disable-description="isMariaCampaignFinished"
      />

      <v-container class="pa-0" fluid>
        <v-row>
          <v-col cols="12">
            <v-card>
              <div class="pt-7 pl-5">
                <span class="section-title pl-0">Campanha</span> <br />
                <p class="subtitle-campaign pt-1">
                  Essa é a primeira e mais importante fase da sua campanha inteligente. É aqui que você conta pra gente o que você precisa promocionar.
                </p>
              </div>
              <mf-tabs-menu v-model="tab" :tabs="permissionedMariaTabs" class="mb-5" />

              <form-maria-campaign-tab1
                v-if="hasOfferSalesPermission"
                ref="formMariaCampaignTab1"
                class="mt-n5"
                :loading="loading"
                :disabled="isMariaCampaignActivated || isMariaCampaignFinished"
                :campaign="campaign"
                :tab="tab"
                :is-maria-campaign-finished="isMariaCampaignFinished"
                @reset-modal-counter="campaign.modalOpenedCounter = 0"
                @save-campaign="tryingToSaveCampaign"
              />

              <form-maria-campaign-tab2
                class="tab-content"
                :disabled="isMariaCampaignActivated || isMariaCampaignFinished"
                :campaign="campaign"
                only-active
                :tab="getCurrentTab()"
              />

              <form-maria-campaign-tab3
                class="tab-content"
                :disabled="isMariaCampaignActivated || isMariaCampaignFinished"
                :campaign="campaign"
                only-active
                :tab="getCurrentTab()"
              />
            </v-card>

            <mf-action-buttons
              v-if="campaign.status"
              class="mt-2"
              :primary-button="{
                text: 'Continuar',
                action: tryingToSaveCampaign,
                isVisible: true,
                isDisabled: loadingSaveDraft,
                isLoading: loadingSaveActive,
              }"
              :secondary-button="secondaryButton"
              :cancel-button="{
                text: isMariaCampaignFinished ? 'Fechar' : 'Cancelar',
                action: redirectCampaigns,
                isVisible: true,
                isDisabled: false,
              }"
            />
          </v-col>
        </v-row>
      </v-container>
    </base-page-layout>
  </mf-loading-dialog>
</template>

<script>
import Swal from 'sweetalert2'
import { DateTime } from 'luxon'
import { required, minLength } from 'vuelidate/lib/validators'

import { notOnlySpace, isFuture } from '@/helpers/validators.js'
import { QUERY_SALES_CAMPAIGN_BY_ID } from '@/modules/campanhas/gestao-ofertas-v2/graphql'
import TitleDescription from '@/modules/campanhas/views/components/edit/TitleDescription.vue'
import { mariaCampaignActions } from '@/modules/maria/campanha-da-maria/mixins/maria-campaign-actions'
import FormMariaCampaignTab1 from '@/modules/maria/campanha-da-maria/views/components/FormMariaCampaignTab1.vue'
import FormMariaCampaignTab2 from '@/modules/maria/campanha-da-maria/views/components/FormMariaCampaignTab2.vue'
import FormMariaCampaignTab3 from '@/modules/maria/campanha-da-maria/views/components/FormMariaCampaignTab3.vue'
import { QUERY_GET_OMNI_CAMPAIGN, QUERY_AGGREGATED_CLIENTS_STATUS_INFO } from '@/modules/maria/campanha-da-maria/graphql'
import offerScheduleCampaign from '@/modules/campanhas/views/mixins/offer-schedule-campaign.js'

export default {
  name: 'FormMariaCampaign',

  components: {
    TitleDescription,
    FormMariaCampaignTab1,
    FormMariaCampaignTab2,
    FormMariaCampaignTab3,
  },

  mixins: [mariaCampaignActions, offerScheduleCampaign],

  data: () => ({
    steppers: [
      {
        step: 1,
        complete: false,
        stepName: 'Escolher produtos',
        pathName: '',
      },
      {
        step: 2,
        complete: false,
        stepName: 'Detalhes dos produtos',
        pathName: '',
      },
      {
        step: 3,
        complete: false,
        stepName: 'Orçamento',
        pathName: '',
      },
      {
        step: 4,
        complete: false,
        stepName: 'Canais',
        pathName: '',
      },
      {
        step: 5,
        complete: false,
        stepName: 'Revisão',
        pathName: '',
      },
    ],
    tab: 0,
    tryingToSaveAsDraft: false,
    loading: false,
    cleanCampaign: {
      title: '',
      description: '',
      products: [],
      segments: [],
      senders: [],
      status: 'DRAFT',
      offer_sales: [],
      takepay_offer_sales: [],
      percentage_offer_sales: [],
      combo_offer_sales: [],
      offerSelected: null,
      initial_date: null,
      final_date: null,
      personalized: false,
      budget: {
        clients: {
          client_active_count: 70,
          client_inactive_count: 15,
          client_lost_count: 10,
          client_never_bought_count: 5,
        }, // uncomment this later
        /* products: {
          rebuy: 80,
          upsell: 20
        }, */ products: {
          rebuy: 100,
          upsell: 0,
        },
        advancedConfigs: {
          trigger: true,
          retrigger: true,
        },
      },
      message: {
        text: '',
        send_date: '',
        send_time: '',
        attachment: {
          data_url: '',
          thumbnail_url: '',
          mimetype: '',
          filename: '',
        },
      },
      modalOpenedCounter: 0,
    },
    campaignInitiation: true,
    offerScheduleFinalizedId: '',
  }),

  computed: {
    hasOfferSalesPermission() {
      return this.$permissions.havePagePermission('campanhas/gestao_ofertas/ofertas') && this.$getAllowedModules()['offer_management']
    },

    secondaryButton() {
      return this.isMariaCampaignFinished
        ? {}
        : {
            text: 'Salvar e sair',
            action: this.tryingToSaveDraft,
            isVisible: !this.isCampaignActivated,
            isDisabled: false,
            isLoading: this.loadingSaveDraft,
          }
    },

    pageTitle() {
      return 'Nova campanha da mar.ia'
    },

    itsGetCampaignFromApi() {
      return this.$route.params.id && this.$route.params.from !== 'Product details'
    },

    errorTitle() {
      if (!this.$v.campaign.title) return ''
      if (this.$v.campaign.title.$error) {
        if (!this.$v.campaign.title.required) return 'Dê um nome para esta campanha.'
        else if (!this.$v.campaign.title.minLength) return 'O título possui menos de 2 caracteres.'
        else if (!this.$v.campaign.title.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      } else return ''
      return ''
    },

    errorDescription() {
      if (!this.$v.campaign.description) return ''
      if (this.$v.campaign.description.$error) {
        if (!this.$v.campaign.description.required) return 'Informe detalhes para melhor identificação da campanha.'
        else if (!this.$v.campaign.description.minLength) return 'A descrição possui menos de 2 caracteres.'
        else if (!this.$v.campaign.description.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      } else return ''
      return ''
    },

    isMultiple() {
      const offers = [
        this.campaign.offer_sales,
        this.campaign.percentage_offer_sales,
        this.campaign.takepay_offer_sales,
        this.campaign.combo_offer_sales,
        this.campaign.cashback_percentage_offer_sales,
        this.campaign.cashback_absolute_offer_sales,
        this.campaign.points_offer_sales,
      ].filter((offer) => offer && offer.length > 0)
      return offers.length > 1
    },

    openMultipleOfferScheduleModal() {
      // has not offerType but is multiple or isEditing
      const isEditing = !!this.campaign._id
      return (!this.campaign.offerType && this.isMultiple) || (isEditing && this.campaign.offerSelected && this.campaign.status !== 'ACTIVE' && this.isMultiple)
    },

    shouldGetOffersFromApi() {
      // backend doesnt save offers from type user hasn't select
      // so if user is editing a supercampaign and change offerType, it will not have products at step 2
      const { offerType, offer_sales, takepay_offer_sales, combo_offer_sales, percentage_offer_sales } = this.campaign
      return (
        (offerType === 'general' && !offer_sales?.length) ||
        (offerType === 'percentage' && !percentage_offer_sales?.length) ||
        (offerType === 'takepay' && !takepay_offer_sales?.length) ||
        (offerType === 'combo' && !combo_offer_sales?.length)
      )
    },

    isNotOfferSchedule() {
      // has not offerSelect but has offer_sales
      return !this.campaign.offerSelected && this.campaign.offer_sales.length > 0
    },

    mariaTabs() {
      return [
        {
          name: 'Gestão de ofertas',
          tabPermission: 'campanhas/gestao_ofertas/ofertas',
          itemsLength: 0,
          haveLength: false,
          disabled: !!(this.campaign.offer_sales.length && !this.campaign.offerSelected),
        },
        {
          name: 'Importação',
          itemsLength: 0,
          haveLength: false,
          disabled: !!this.campaign.offerSelected,
        },
        {
          name: 'Busca',
          itemsLength: 0,
          haveLength: false,
          disabled: !!this.campaign.offerSelected,
        },
      ]
    },
    permissionedMariaTabs() {
      let tabs = this.mariaTabs
      if (!this.hasOfferSalesPermission) {
        tabs.shift()
      }
      return tabs
    },
  },

  watch: {
    async 'campaign.offerSelected'(value) {
      if (!this.campaignInitiation) {
        if (value) {
          await this.getData()
        } else {
          this.campaign = {
            ...this.cleanCampaign,
            _id: this.campaign._id,
            title: this.campaign.title,
            description: this.campaign.description,
            modalOpenedCounter: 0,
          }
        }
      }
    },
  },

  async mounted() {
    this.loadingPage = true
    window.scrollTo(0, 0)
    if (this.itsGetCampaignFromApi) await this.getCampaign()
    this.campaign = {
      ...this.campaign,
      ...(this.itsGetCampaignFromApi ? {} : this.mariaCampaign),
    }
    await this.setTab()
    this.campaignInitiation = false
    this.loadingPage = false
  },

  methods: {
    getPushAction({ link, type, reference_id }) {
      let formattedType = ''
      if (type === 'EXTERNAL_LINK') formattedType = 'EXTERNAL'
      else formattedType = 'INTERNAL'

      return {
        link,
        reference_id,
        type: formattedType,
      }
    },
    getOffertype(campaign) {
      const { combo_offer_sales, offer_sales, percentage_offer_sales, takepay_offer_sales } = campaign
      if (offer_sales.length) return 'general'
      if (combo_offer_sales.length) return 'combo'
      if (percentage_offer_sales.length) return 'percentage'
      if (takepay_offer_sales.length) return 'takepay'
    },
    async getOffersFromApi() {
      try {
        this.loading = true
        await this.$apollo
          .query({
            query: QUERY_SALES_CAMPAIGN_BY_ID,
            variables: {
              id: this.campaign.offerSelected,
            },
            context: {
              uri: this.$microservicesUrls['sales'],
            },
            fetchPolicy: 'no-cache',
          })
          .then(async (response) => {
            const { offer_sales, percentage_offer_sales, takepay_offer_sales, combo_offer_sales, offerType } = this.campaign
            this.campaign = {
              ...this.campaign,
              offer_sales:
                offer_sales && offer_sales.length && offerType === 'general' ? offer_sales : this.formatOfferSales(response.data.offerSchedule.offer_sales),
              percentage_offer_sales:
                percentage_offer_sales && percentage_offer_sales.length && offerType === 'percentage'
                  ? percentage_offer_sales
                  : this.formatOfferSalesPercentage(response.data.offerSchedule.percentage_offer_sales),
              takepay_offer_sales:
                takepay_offer_sales && takepay_offer_sales.length && offerType === 'takepay'
                  ? takepay_offer_sales
                  : this.formatOfferSalesTakeAndPay(response.data.offerSchedule.takepay_offer_sales),
              combo_offer_sales:
                combo_offer_sales && combo_offer_sales.length && offerType === 'combo'
                  ? combo_offer_sales
                  : this.formatOfferSalesCombo(response.data.offerSchedule.combo_offer_sales),
            }
            await this.recalculateTargets()
            this.loading = false
          })
      } catch (error) {
        console.error(error)
        this.loading = false
        this.$snackbar({ message: 'Erro ao buscar dados no servidor', snackbarColor: 'error' })
      }
    },

    async getData() {
      try {
        this.loading = true
        await this.$apollo
          .query({
            query: QUERY_SALES_CAMPAIGN_BY_ID,
            variables: {
              id: this.campaign.offerSelected,
            },
            context: {
              uri: this.$microservicesUrls['sales'],
            },
            fetchPolicy: 'no-cache',
          })
          .then(async (response) => {
            this.campaign = {
              ...this.campaign,
              ...response.data.offerSchedule,
              _id: this.campaign._id,
              initial_date: this.campaignInitiation ? this.campaign.initial_date : this.setInitialDate(response.data.offerSchedule.start_date),
              final_date: response.data.offerSchedule.end_date,
              status: this.campaign.status,
              description: this.campaign.description,
              offer_sales: this.campaignInitiation ? this.campaign.offer_sales : this.formatOfferSales(response.data.offerSchedule.offer_sales),
              percentage_offer_sales: this.campaignInitiation
                ? this.campanha.percentage_offer_sales
                : this.formatOfferSalesPercentage(response.data.offerSchedule.percentage_offer_sales),
              takepay_offer_sales: this.campaignInitiation
                ? this.campanha.takepay_offer_sales
                : this.formatOfferSalesTakeAndPay(response.data.offerSchedule.takepay_offer_sales),
              combo_offer_sales: this.campaignInitiation
                ? this.campanha.combo_offer_sales
                : this.formatOfferSalesCombo(response.data.offerSchedule.combo_offer_sales),
              units: this.campaignInitiation ? this.campaign.units : response.data.offerSchedule.units,
              units_configuration: this.campaignInitiation
                ? this.campaign.units_configuration
                : response.data.offerSchedule.units.map((unit) => {
                    return { value: unit.id_erp, dataType: unit.dataType }
                  }),
              modalOpenedCounter: 0,
            }
            this.campaign.units = this.campaign.units.map((unit) => (unit.id_erp ? unit.id_erp : unit))
            this.automaticCampaign = Boolean(['erp'].includes(this.campaign?.origin))
            this.campaign.eans = this.campaign.offer_sales.map((offer) => offer.eans)
            await this.recalculateTargets()
            this.loading = false
          })
      } catch (error) {
        console.error(error)
        this.loading = false
        this.$snackbar({ message: 'Erro ao buscar dados no servidor', snackbarColor: 'error' })
      }
    },

    async recalculateTargets() {
      if (this.campaign.segments.length) {
        try {
          const {
            data: { getAggregatedClientStatusInfo },
          } = await this.$apollo.query({
            query: QUERY_AGGREGATED_CLIENTS_STATUS_INFO,
            variables: {
              segments_ids: [this.campaign.segments[0]._id],
            },
            fetchPolicy: 'no-cache',
          })
          const { client_active_count, client_inactive_count, client_lost_count, client_never_bought_count, total_count } = getAggregatedClientStatusInfo.all
          this.campaign.budget.clients = {
            client_inactive_count: Math.round((client_inactive_count * 100) / total_count),
            client_active_count: Math.round((client_active_count * 100) / total_count),
            client_lost_count: Math.round((client_lost_count * 100) / total_count),
            client_never_bought_count: Math.round((client_never_bought_count * 100) / total_count),
          }
        } catch (error) {
          console.error(error)
          this.$snackbar({ message: 'Erro ao buscar dados no servidor', snackbarColor: 'error' })
        }
      } else {
        this.campaign.budget.clients = {
          client_inactive_count: 15,
          client_active_count: 70,
          client_lost_count: 10,
          client_never_bought_count: 5,
        }
      }
    },

    getCurrentTab() {
      return this.$permissions.havePagePermission('campanhas/gestao_ofertas/ofertas') && this.$getAllowedModules()['offer_management'] ? this.tab : this.tab + 1
    },

    validateInfos() {
      this.allInputsRequired = true
      this.$v.$reset()
      this.$v.$touch()
      if (this.$v.$error) {
        this.$nextTick(() => {
          this.$vuetify.goTo(document.querySelector('.error--text'), { offset: 200 })
        })
        return false
      }

      if (this.campaign.offer_sales.length > 1000) {
        this.$snackbar({ message: 'Por questões de performance, não é permitido selecionar mais de 1000 produtos nessa fase.', snackbarColor: 'error' })
        return false
      }

      if (!this.campaign.offerSelected && !this.campaign.offer_sales.length && !this.tryingToSaveAsDraft) {
        this.$snackbar({ message: 'Selecione uma oferta ou um produto.', snackbarColor: 'error' })
        return false
      }

      return true
    },

    setInitialDate(initialDate) {
      if (!initialDate) return initialDate
      if (!isFuture(initialDate)) {
        return DateTime.now().toFormat('yyyy-MM-dd')
      }
      return initialDate
    },

    async getCampaign() {
      await this.$apollo
        .query({
          query: QUERY_GET_OMNI_CAMPAIGN,
          context: {
            uri: this.$microservicesUrls['campaigns'],
          },
          variables: {
            id: this.$route.params.id,
          },
          fetchPolicy: 'no-cache',
        })
        .then(({ data: { omniCampaign } }) => {
          this.offerScheduleFinalizedId = omniCampaign?.offer_schedule?.finalized ? omniCampaign.offer_schedule._id : ''
          this.campaign = {
            offerSelected: omniCampaign.offer_schedule ? omniCampaign.offer_schedule._id : null,
            ...omniCampaign,
            version: omniCampaign.version ?? 1,
            modalOpenedCounter: 0,
            initial_date: omniCampaign.initial_date ? DateTime.fromISO(omniCampaign.initial_date).toFormat('yyyy-MM-dd') : '',
            final_date: omniCampaign.final_date ? DateTime.fromISO(omniCampaign.final_date).toFormat('yyyy-MM-dd') : '',
            offer_sales: this.formatOfferSales(omniCampaign.offer_sales),
            percentage_offer_sales: this.formatOfferSalesPercentage(omniCampaign.percentage_offer_sales),
            takepay_offer_sales: this.formatOfferSalesTakeAndPay(omniCampaign.takepay_offer_sales),
            offerType: this.getOffertype(omniCampaign),
            units: omniCampaign.units.map((unit) => unit.id_erp).filter((unit) => unit),
            units_configuration: omniCampaign.units.map((unit) => {
              return { value: unit.id_erp, dataType: unit.dataType }
            }),
            personalized: !!omniCampaign?.offer_schedule?.segments.length,
            senders: omniCampaign.channels?.whatsapp ? omniCampaign.channels?.whatsapp?.senders : [],
            budget: {
              advancedConfigs: {
                trigger:
                  omniCampaign.advanced_configuration?.multiple_channels_per_consumer === undefined
                    ? true
                    : omniCampaign.advanced_configuration?.multiple_channels_per_consumer,
                retrigger:
                  omniCampaign.advanced_configuration?.multiple_messages_per_consumer === undefined
                    ? true
                    : omniCampaign.advanced_configuration?.multiple_messages_per_consumer,
              },
              clients: {
                client_active_count: this.getClientTargetsFormatted(omniCampaign.client_targets)['client_active_count'],
                client_inactive_count: this.getClientTargetsFormatted(omniCampaign.client_targets)['client_inactive_count'],
                client_lost_count: this.getClientTargetsFormatted(omniCampaign.client_targets)['client_lost_count'],
                client_never_bought_count: this.getClientTargetsFormatted(omniCampaign.client_targets)['client_never_bought_count'],
              },
              clientsDistribution: {
                actives: this.getClientTargetsV2Formatted(omniCampaign.client_targets_distribution)['actives'],
                recent_inactives: this.getClientTargetsV2Formatted(omniCampaign.client_targets_distribution)['recent_inactives'],
                losses: this.getClientTargetsV2Formatted(omniCampaign.client_targets_distribution)['losses'],
                at_risk: this.getClientTargetsV2Formatted(omniCampaign.client_targets_distribution)['at_risk'],
                never_bought: this.getClientTargetsV2Formatted(omniCampaign.client_targets_distribution)['never_bought'],
              },
              products: {
                rebuy: omniCampaign.product_targets?.repurchase ? omniCampaign.product_targets?.repurchase * 100 : 100,
                upsell: omniCampaign.product_targets?.upsell ? omniCampaign.product_targets?.upsell * 100 : 0,
              },
            },
            shotConfiguration: {
              whatsapp: {
                name: 'Whatsapp',
                icon: 'whatsapp',
                appChannelOptions: {
                  toggleAppActivated: omniCampaign.channels?.whatsapp ? omniCampaign.channels.whatsapp.active : false,
                  toggleAppExpanded: false,
                },
                targeting_mode: omniCampaign.channels?.whatsapp ? omniCampaign.channels.whatsapp.targeting_mode : '',
                credits: omniCampaign.channels?.whatsapp ? omniCampaign.channels.whatsapp.budget.toString() : '',
                message: {
                  text: omniCampaign.channels?.whatsapp ? omniCampaign.channels.whatsapp.message.text : '',
                  send_date: omniCampaign.channels?.whatsapp ? omniCampaign.channels.whatsapp.message.send_date : '',
                  attachment: {
                    data_url: omniCampaign.channels?.whatsapp?.message?.attachment ? omniCampaign.channels.whatsapp.message.attachment.data_url : '',
                    thumbnail_url: omniCampaign.channels?.whatsapp?.message?.attachment ? omniCampaign.channels.whatsapp.message.attachment.thumbnail_url : '',
                    mimetype: omniCampaign.channels?.whatsapp?.message?.attachment ? omniCampaign.channels.whatsapp.message.attachment.mimetype : '',
                    filename: omniCampaign.channels?.whatsapp?.message?.attachment ? omniCampaign.channels.whatsapp.message.attachment.filename : '',
                  },
                  type: omniCampaign.channels?.whatsapp?.message?.type ? omniCampaign.channels?.whatsapp?.message?.type : '',
                },
              },
              sms: {
                name: 'SMS',
                icon: 'sms',
                appChannelOptions: {
                  toggleAppActivated: omniCampaign.channels?.sms ? omniCampaign.channels.sms.active : false,
                  toggleAppExpanded: false,
                },
                send_date: omniCampaign.channels?.sms ? omniCampaign.channels.sms.message.send_date : '',
                text: omniCampaign.channels?.sms ? omniCampaign.channels.sms.message.text : '',
                credits: omniCampaign.channels?.sms ? omniCampaign.channels.sms.budget.toString() : '',
                error: false,
              },
              push: {
                name: 'Push',
                icon: 'smartphone',
                appChannelOptions: {
                  toggleAppActivated: omniCampaign.channels?.push ? omniCampaign.channels.push.active : false,
                  toggleAppExpanded: false,
                },
                title: omniCampaign.channels?.push ? omniCampaign.channels.push.message.title : '',
                send_date: omniCampaign.channels?.push ? omniCampaign.channels.push.message.send_date : '',
                text: omniCampaign.channels?.push ? omniCampaign.channels.push.message.text : '',
                credits: omniCampaign.channels?.push ? omniCampaign.channels.push.budget.toString() : '',
                inner_message_title: omniCampaign.channels?.push ? omniCampaign.channels.push.message.inner_message_title : '',
                inner_message_text: omniCampaign.channels?.push ? omniCampaign.channels.push.message.inner_message_text : '',
                innerMessage: omniCampaign.channels?.push?.message?.inner_message_text !== omniCampaign.channels?.push?.message?.text,
                image_url: omniCampaign.channels?.push?.message?.image_url ?? null,
                action: omniCampaign.channels?.push?.message?.action
                  ? this.getPushAction(omniCampaign.channels.push.message.action)
                  : {
                      link: 'SalesOmniCampaignScreen',
                      reference_id: '',
                      type: 'INTERNAL',
                    },
              },
              email: {
                name: 'E-mail',
                icon: 'email',
                appChannelOptions: {
                  toggleAppActivated: omniCampaign.channels?.email ? omniCampaign.channels.email.active : false,
                  toggleAppExpanded: false,
                },
                credits: omniCampaign.channels?.email ? omniCampaign.channels.email.budget.toString() : '',
                message: {
                  send_date: omniCampaign.channels?.email?.message ? omniCampaign.channels.email.message.send_date : '',
                  email_configuration_type: omniCampaign.channels?.email?.message ? omniCampaign.channels.email.message.email_configuration_type : '',
                  processed_html: omniCampaign.channels?.email?.message ? omniCampaign.channels.email.message.processed_html : '',
                  subject: omniCampaign.channels?.email?.message ? omniCampaign.channels.email.message.subject : '',
                  simple_email_configuration: {
                    subject: omniCampaign.channels?.email?.message ? omniCampaign.channels.email.message?.simple_email_configuration?.subject : '',
                    header_image: omniCampaign.channels?.email?.message ? omniCampaign.channels.email.message?.simple_email_configuration?.header_image : '',
                    text: omniCampaign.channels?.email?.message ? omniCampaign.channels.email.message?.simple_email_configuration?.text : '',
                    image: omniCampaign.channels?.email?.message ? omniCampaign.channels.email.message?.simple_email_configuration?.image : '',
                    click_to_call_button_link: omniCampaign.channels?.email?.message
                      ? omniCampaign.channels.email.message?.simple_email_configuration?.click_to_call_button_link
                      : '',
                    click_to_call_button_text: omniCampaign.channels?.email?.message
                      ? omniCampaign.channels.email.message?.simple_email_configuration?.click_to_call_button_text
                      : '',
                    footer: omniCampaign.channels?.email?.message ? omniCampaign.channels.email.message?.simple_email_configuration?.footer : '',
                  },
                  html_email_configuration: {
                    url: omniCampaign.channels?.email?.message ? omniCampaign.channels.email.message?.html_email_configuration?.url : '',
                    filename: omniCampaign.channels?.email?.message ? omniCampaign.channels.email.message?.html_email_configuration?.filename : '',
                    has_uploaded_file: omniCampaign.channels?.email?.message
                      ? omniCampaign.channels.email.message?.html_email_configuration?.has_uploaded_file
                      : '',
                    uploaded_at: omniCampaign.channels?.email?.message ? omniCampaign.channels.email.message?.html_email_configuration?.uploaded_at : '',
                  },
                  builder_email_configuration: omniCampaign.channels?.email?.message?.builder_email_configuration,
                },
              },
            },
          }
        })
        .catch((error) => {
          console.error(error)
          Swal.fire({
            text: 'Erro ao buscar campanha',
            icon: 'error',
            showConfirmButton: false,
            timer: 1000,
            customClass: {
              content: 'content-class',
            },
          })
        })
    },

    setTab() {
      if (this.campaign.offerSelected || (!this.campaign.offer_sales.length && !this.campaign.offerSelected)) this.tab = 0
      else this.tab = 2
    },

    async tryingToSaveCampaign() {
      const isValid = this.validateInfos()
      if (!isValid) return
      if (this.openMultipleOfferScheduleModal && this.campaign.modalOpenedCounter < 1) {
        this.$refs.formMariaCampaignTab1.campaignId = this.campaign.offerSelected
        this.$refs.formMariaCampaignTab1.isOfferDialogOpen = true
        this.campaign.modalOpenedCounter++
        return
      } else if (this.isNotOfferSchedule) {
        this.campaign.offerType = 'general'
      } else {
        if (!this.campaign.offerType) this.campaign.offerType = this.getOffertype(this.campaign)
      }

      if (!this.campaign.offerType) return

      this.loadingSaveActive = true
      try {
        await this.saveCampaign()
      } catch (e) {
        this.$snackbar({ message: 'Ocorreu um erro ao salvar campanha.', snackbarColor: 'error' })
        this.$nextTick(() => {
          this.$vuetify.goTo(document.querySelector('.error--text'), { offset: 200 })
        })
      } finally {
        this.loadingSaveActive = false
      }
    },

    async tryingToSaveDraft() {
      if (this.openMultipleOfferScheduleModal && this.campaign.modalOpenedCounter < 1) {
        this.$refs.formMariaCampaignTab1.campaignId = this.campaign.offerSelected
        this.$refs.formMariaCampaignTab1.isOfferDialogOpen = true
        this.campaign.modalOpenedCounter++
        return
      } else if (this.isNotOfferSchedule) this.campaign.offerType = 'general'
      this.loadingSaveDraft = true
      try {
        await this.saveDraft()
      } catch (e) {
        this.$snackbar({ message: 'Ocorreu um erro ao salvar campanha.', snackbarColor: 'error' })
        this.$nextTick(() => {
          this.$vuetify.goTo(document.querySelector('.error--text'), { offset: 200 })
        })
      } finally {
        this.loadingSaveDraft = false
      }
    },

    async saveCampaign() {
      if (this.shouldGetOffersFromApi) await this.getOffersFromApi()
      this.SET_MARIA_CAMPAIGN(this.campaign)
      this.forcePush = true
      this.$router.push({ name: 'Product details' })
    },

    async saveDraft() {
      if (this.shouldGetOffersFromApi) await this.getOffersFromApi()
      await this.upsertDraftMariaCampaign(
        {
          ...this.campaign,
          offer_sales: this.formatOfferSales(this.campaign.offer_sales),
          percentage_offer_sales: this.formatOfferSalesPercentage(this.campaign.percentage_offer_sales),
          takepay_offer_sales: this.formatOfferSalesTakeAndPay(this.campaign.takepay_offer_sales),
          combo_offer_sales: this.formatOfferSalesCombo(this.campaign.combo_offer_sales),
          ...this.getPartialPayload(),
        },
        this.isCampaignActivated
      )
    },

    getClientTargetsFormatted(targets) {
      if (targets) {
        const hasTargets = !Object.values(targets)
          .slice(0, -1)
          .every((target) => target === 0)
        if (hasTargets) {
          return {
            client_active_count: targets.active * 100,
            client_inactive_count: targets.inactive * 100,
            client_lost_count: targets.lost * 100,
            client_never_bought_count: targets.never_bought * 100,
          }
        }
      }
      return {
        client_active_count: 70,
        client_inactive_count: 15,
        client_lost_count: 10,
        client_never_bought_count: 5,
      }
    },

    getClientTargetsV2Formatted(targets) {
      if (targets) {
        const hasTargets = !Object.values(targets)
          .slice(0, -1)
          .every((target) => target === 0)
        if (hasTargets) {
          return {
            actives: targets.actives * 100,
            recent_inactives: targets.recent_inactives * 100,
            losses: targets.losses * 100,
            at_risk: targets.at_risk * 100,
            never_bought: targets.never_bought * 100,
          }
        }
      }
      return {
        actives: 60,
        recent_inactives: 15,
        losses: 10,
        at_risk: 10,
        never_bought: 5,
      }
    },

    getFormattedItems(products) {
      if (!products && !products.length) return []
      const arrGroups = products.reduce((obj, item) => {
        if (item.isFormated) {
          if (!obj['products']) obj['products'] = []
          obj['products'].push(item)
          return obj
        }
        if (item.isGroup) {
          if (!obj[item.nameGroup]) obj[item.nameGroup] = []
          obj[item.nameGroup].push(item)
          return obj
        } else {
          item = {
            active: typeof item.active === 'boolean' ? item.active : true,
            ...item,
          }
          if (!obj['products']) obj['products'] = []
          obj['products'].push(item)
          return obj
        }
      }, {})

      const formattedItems = [
        ...Object.keys(arrGroups)
          .filter((key) => key !== 'products')
          .map((key) => {
            return {
              name: arrGroups[key][0].nameGroup,
              isGroup: arrGroups[key][0].isGroup,
              products: arrGroups[key],
              price_1: arrGroups[key][0].price_1,
              price_2: arrGroups[key][0].price_2,
              percentage_discount: arrGroups[key][0].percentage_discount ? arrGroups[key][0].percentage_discount : null,
              take: arrGroups[key][0].take ? arrGroups[key][0].take : null,
              pay: arrGroups[key][0].pay ? arrGroups[key][0].pay : null,
              ...arrGroups[key][0].groupInfo,
            }
          }),
      ]

      const newProducts = arrGroups['products'] ? [...arrGroups['products'], ...formattedItems] : formattedItems
      return newProducts.map((product) => ({ ...product, isFormated: true }))
    },
  },

  validations() {
    if (this.tryingToSaveAsDraft) {
      return {
        campaign: {
          title: {
            required,
            minLength: minLength(2),
            notOnlySpace,
          },
        },
      }
    } else {
      return {
        campaign: {
          title: {
            required,
            minLength: minLength(2),
            notOnlySpace,
          },
          description: {
            required,
          },
        },
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.v-application--is-ltr
  .v-tabs--align-with-title
  > .v-tabs-bar:not(.v-tabs-bar--show-arrows)
  > .v-slide-group__wrapper
  > .v-tabs-bar__content
  > .v-tabs-slider-wrapper
  + .v-tab {
  margin-left: 0 !important;
}

.v-tabs {
  border-bottom: 1px solid $cinza-l60;
}

.v-tab {
  text-transform: initial;
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 120%;
  color: $marca-1;
}
.theme--light.v-tabs > .v-tabs-bar .v-tab--disabled,
.theme--light.v-tabs > .v-tabs-bar .v-tab:not(.v-tab--active),
.theme--light.v-tabs > .v-tabs-bar .v-tab:not(.v-tab--active) > .mf-icon {
  color: $cinza-d10;
}
.tabs-button-header {
  position: relative;
  .button-new {
    position: absolute;
    top: 5px;
    right: 0;
  }
}

.subtitle-campaign {
  width: 100%;
  word-break: break-word;
}

.content-class {
  font-family: 'Lato', sans-serif !important;
}

.tab-content {
  min-height: 450px;
}
</style>
